/**
 * @engine/input — ButtonControl
 *
 * Binary button control with press/release tracking.
 */

import type { ControlType, ButtonState } from '../types';
import { InputControl } from './InputControl';

/**
 * Button control — binary pressed/released state with timing.
 */
export class ButtonControl extends InputControl<boolean> implements ButtonState {
  readonly type: ControlType = 'button';

  /** Press threshold for analog buttons (0..1) */
  private _pressThreshold: number;

  /** Time when the button was pressed */
  private _pressTime: number = 0;

  constructor(name: string, pressThreshold: number = 0.5) {
    super(name, false);
    this._pressThreshold = pressThreshold;
  }

  // ============================================================================
  // ButtonState interface
  // ============================================================================

  get isPressed(): boolean {
    return this._value;
  }

  get wasPressed(): boolean {
    return this._value && !this._previousValue;
  }

  get wasReleased(): boolean {
    return !this._value && this._previousValue;
  }

  get holdTime(): number {
    if (!this._value) return 0;
    return this._lastUpdateTime - this._pressTime;
  }

  // ============================================================================
  // Update (supports analog input)
  // ============================================================================

  /**
   * Update from boolean input
   */
  override update(pressed: boolean, time: number): void {
    if (pressed && !this._value) {
      this._pressTime = time;
    }
    super.update(pressed, time);
  }

  /**
   * Update from analog input (e.g., trigger)
   */
  updateAnalog(value: number, time: number): void {
    this.update(value >= this._pressThreshold, time);
  }

  /**
   * Get raw analog value (for triggers)
   */
  private _analogValue: number = 0;

  get analogValue(): number {
    return this._analogValue;
  }

  setAnalogValue(value: number, time: number): void {
    this._analogValue = value;
    this.updateAnalog(value, time);
  }
}
